(() => {
  'use strict';
  if (window.OrbitStoreMotion) { window.OrbitStoreMotion.refresh(document); return; }
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const saveData = !!navigator.connection?.saveData;
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const seen = new WeakSet();
  const tracked = new Set();
  const active = new Map();
  const queuedRoots = new Set();
  let enabled = false, observer, mutation, scanFrame = 0, parallaxFrame = 0, epoch = 0;
  let hero, heroImage, heroVisible = false, heroObserver;
  const selector = [
    '.bh-hero__visual','.bh-heading','.bh-category-grid > li','.bh-product-grid > li',
    '.bh-routine__art','.bh-routine__copy','.bh-story',
    '.bc-hero__copy','.bc-hero__visual','#product-grid > .grid__item',
    '.beauty-product .product__media-wrapper','.beauty-product .product__title',
    '.beauty-product-help','.beauty-product-details',
    '.beauty-routine__heading','.beauty-routine-option',
    '.bf-newsletter__copy','.bf-brand','.bf-nav','.bf-help'
  ].join(',');

  const find = (root, query) => [
    ...(root.nodeType === 1 && root.matches(query) ? [root] : []),
    ...root.querySelectorAll(query)
  ];
  const finish = element => {
    const animation = active.get(element);
    if (animation) { animation.cancel(); active.delete(element); }
  };
  function animate(element, frames, duration = 560, delay = 0) {
    if (!enabled || !element.isConnected || !element.animate || element.closest('[hidden],cart-drawer,quick-add-modal')) return;
    finish(element);
    const animation = element.animate(frames, { duration, delay, easing: ease, fill: 'backwards' });
    active.set(element, animation);
    animation.onfinish = () => {
      if (active.get(element) === animation) { animation.cancel(); active.delete(element); }
    };
    animation.oncancel = () => { if (active.get(element) === animation) active.delete(element); };
  }
  function rise(element, delay = 0, amount = 16) {
    animate(element, [
      { opacity: .15, transform: `translate3d(0,${amount}px,0)` },
      { opacity: 1, transform: 'translate3d(0,0,0)' }
    ], desktop.matches ? 800 : 580, delay);
  }
  function headline(heading) {
    if (!heading || seen.has(heading)) return;
    seen.add(heading);
    if (!heading.querySelector('.bm-word')) {
      const fragment = document.createDocumentFragment();
      for (const token of heading.textContent.match(/\S+|\s+/gu) || []) {
        if (/^\s+$/u.test(token)) fragment.append(document.createTextNode(token));
        else { const word = document.createElement('span'); word.className = 'bm-word'; word.textContent = token; fragment.append(word); }
      }
      heading.replaceChildren(fragment);
    }
    heading.querySelectorAll('.bm-word').forEach((word, index) => animate(word, [
      { opacity: .65, transform: 'translate3d(0,24px,0)', filter: desktop.matches && !saveData ? 'blur(2px)' : 'none' },
      { opacity: 1, transform: 'translate3d(0,0,0)', filter: 'none' }
    ], desktop.matches ? 1000 : 720, 140 + Math.min(index * 90, 360)));
  }
  function intro(scope) {
    for (const section of find(scope, '.bh-hero')) {
      if (seen.has(section) || section.getBoundingClientRect().top > innerHeight) continue;
      seen.add(section);
      const eyebrow = section.querySelector('.bh-eyebrow'); if (eyebrow) rise(eyebrow, 40, 12);
      headline(section.querySelector('h1'));
      const description = section.querySelector('.bh-hero__description'); if (description) rise(description, 220);
      const actions = section.querySelector('.bh-actions');
      if (actions) animate(actions, [{ opacity: .85, transform: 'translate3d(0,8px,0)' }, { opacity: 1, transform: 'translate3d(0,0,0)' }], 480, 180);
    }
  }
  function heroEntrance(element) {
    const image = element.querySelector('.bh-hero__photo');
    const initialEpoch = epoch;
    const play = () => {
      if (!enabled || initialEpoch !== epoch || !element.isConnected) return;
      const rect = element.getBoundingClientRect();
      if (rect.top >= innerHeight - 48 || rect.bottom <= 0) {
        seen.delete(element); tracked.add(element); observer?.observe(element); return;
      }
      if (element.contains(document.activeElement)) return;
      animate(element, [{ clipPath: 'inset(14% 5% 14% 5%)' }, { clipPath: 'inset(0% 0% 0% 0%)' }], desktop.matches ? 1400 : 1000);
      if (image && !saveData) animate(image, [{ transform: 'scale(1.065)' }, { transform: 'scale(1)' }], desktop.matches ? 3200 : 2200);
    };
    const afterPaint = () => requestAnimationFrame(() => requestAnimationFrame(play));
    if (image && !image.complete && image.decode) image.decode().catch(() => {}).then(afterPaint);
    else afterPaint();
  }
  function reveal(element) {
    if (seen.has(element) || !element.getClientRects().length) return;
    seen.add(element); tracked.delete(element); observer?.unobserve(element);
    if (element.contains(document.activeElement)) return;
    if (element.matches('.bh-hero__visual')) { heroEntrance(element); return; }
    const siblings = element.parentElement ? Array.from(element.parentElement.children) : [];
    const isCard = element.matches('.grid__item,.bh-category-grid > li,.bh-product-grid > li,.beauty-routine-option');
    const template = element.parentElement ? getComputedStyle(element.parentElement).gridTemplateColumns : 'none';
    const columns = template === 'none' ? 1 : Math.max(1, template.trim().split(/\s+/).length);
    const delay = isCard ? Math.min(Math.max(0, siblings.indexOf(element)) % columns, 3) * 85 : 0;
    if (element.matches('.bh-routine__art,.bc-hero__visual')) {
      animate(element, [{ opacity: .5, clipPath: 'inset(7% 0% 7% 0%)' }, { opacity: 1, clipPath: 'inset(0% 0% 0% 0%)' }], 760, delay);
    } else rise(element, delay, isCard ? (desktop.matches ? 28 : 18) : 20);
  }
  function scan(scope = document) {
    if (!enabled || !scope.querySelectorAll) return;
    intro(scope);
    for (const element of find(scope, selector)) {
      if (seen.has(element) || tracked.has(element) || element.closest('[hidden],cart-drawer,quick-add-modal')) continue;
      if (observer) { tracked.add(element); observer.observe(element); }
    }
  }
  function updateParallax() {
    parallaxFrame = 0;
    if (!enabled || !desktop.matches || saveData || document.hidden || !heroVisible || !heroImage?.isConnected) return;
    const rect = hero.getBoundingClientRect();
    const y = Math.max(-16, Math.min(16, (innerHeight / 2 - rect.top - rect.height / 2) / Math.max(rect.height, 1) * 32));
    heroImage.style.setProperty('--bm-parallax', `${y.toFixed(2)}px`);
  }
  function scheduleParallax() { if (!parallaxFrame && enabled && heroVisible) parallaxFrame = requestAnimationFrame(updateParallax); }
  function setupParallax() {
    heroObserver?.disconnect();
    hero = document.querySelector('.bh-hero__visual'); heroImage = hero?.querySelector('.bh-hero__photo');
    const allowed = enabled && desktop.matches && !saveData && heroImage && 'IntersectionObserver' in window;
    document.documentElement.classList.toggle('beauty-motion-parallax', !!allowed);
    if (!allowed) { heroImage?.style.removeProperty('--bm-parallax'); heroVisible = false; return; }
    heroObserver = new IntersectionObserver(entries => { heroVisible = entries[0].isIntersecting; if (heroVisible) scheduleParallax(); });
    heroObserver.observe(hero);
  }
  function flushRoots() {
    scanFrame = 0;
    for (const root of queuedRoots) if (root.isConnected) scan(root);
    queuedRoots.clear();
    for (const element of tracked) if (!element.isConnected) { observer?.unobserve(element); tracked.delete(element); }
    for (const element of active.keys()) if (!element.isConnected) finish(element);
  }
  function start() {
    if (enabled || reduce.matches) return;
    enabled = true; document.documentElement.classList.add('beauty-motion-on');
    if ('IntersectionObserver' in window) observer = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) reveal(entry.target);
    }, { threshold: .12, rootMargin: '0px 0px -48px 0px' });
    scan(document); setupParallax();
    if ('MutationObserver' in window) {
      mutation = new MutationObserver(records => {
        for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1 && !node.classList.contains('bm-word')) queuedRoots.add(node);
        if (!scanFrame && (queuedRoots.size || records.some(r => r.removedNodes.length))) scanFrame = requestAnimationFrame(flushRoots);
      });
      mutation.observe(document.getElementById('MainContent') || document.querySelector('main') || document.body, { childList: true, subtree: true });
    }
  }
  function stop() {
    enabled = false; epoch++; observer?.disconnect(); heroObserver?.disconnect(); mutation?.disconnect();
    for (const element of [...active.keys()]) finish(element);
    tracked.clear(); queuedRoots.clear();
    if (scanFrame) cancelAnimationFrame(scanFrame); scanFrame = 0;
    if (parallaxFrame) cancelAnimationFrame(parallaxFrame); parallaxFrame = 0;
    document.documentElement.classList.remove('beauty-motion-on','beauty-motion-parallax');
    heroImage?.style.removeProperty('--bm-parallax'); heroVisible = false;
  }
  function settleTarget(event) {
    if (!(event.target instanceof Element)) return;
    for (const element of [...active.keys()]) if (element === event.target || element.contains(event.target)) finish(element);
  }
  function refresh(scope = document) { if (enabled) { scan(scope); setupParallax(); } }
  const sectionLoad = event => refresh(event.target);
  const sectionUnload = event => {
    for (const element of [...active.keys()]) if (event.target.contains(element)) finish(element);
    for (const element of tracked) if (event.target.contains(element)) { observer?.unobserve(element); tracked.delete(element); }
  };
  const preferenceChange = () => reduce.matches ? stop() : start();
  const resize = () => { if (enabled) setupParallax(); };
  const visibility = () => { if (!document.hidden) scheduleParallax(); };
  reduce.addEventListener('change', preferenceChange); desktop.addEventListener('change', resize);
  window.addEventListener('scroll', scheduleParallax, { passive: true });
  document.addEventListener('visibilitychange', visibility);
  document.addEventListener('focusin', settleTarget, true); document.addEventListener('pointerdown', settleTarget, true);
  document.addEventListener('shopify:section:load', sectionLoad); document.addEventListener('shopify:section:unload', sectionUnload);
  document.addEventListener('shopify:section:select', sectionUnload);
  window.OrbitStoreMotion = {
    refresh,
    get activeAnimations() { return active.size; },
    get observedElements() { return tracked.size; },
    destroy() {
      stop(); reduce.removeEventListener('change', preferenceChange); desktop.removeEventListener('change', resize);
      window.removeEventListener('scroll', scheduleParallax); document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('focusin', settleTarget, true); document.removeEventListener('pointerdown', settleTarget, true);
      document.removeEventListener('shopify:section:load', sectionLoad); document.removeEventListener('shopify:section:unload', sectionUnload);
      document.removeEventListener('shopify:section:select', sectionUnload); document.removeEventListener('DOMContentLoaded', start);
      delete window.OrbitStoreMotion;
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();

